import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Shield, UsersRound, ShoppingCart, Store,
  Contact, Package, Warehouse, BarChart3, Bell, Search, LogOut,
  ChevronDown, ChevronRight, Menu, X, BookOpen, Settings, Tag, UserCheck, Truck, ShieldAlert, PackageOpen, User, KeyRound, RotateCcw, FileText, Trash2, CreditCard, Inbox, TrendingUp, Factory, ClipboardList, MapPin, Layers, Settings2, Link2, Upload
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// Permission-driven sidebar navigation — shows items based on user's resolved permissions
function getNavForPermissions(permissions = [], roles = []) {
  const has = (...keys) => keys.some(k => permissions.includes(k));
  const groups = [];

  // Dashboard — always visible
  groups.push({ label: null, items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard" }] });

  // Admin Setup
  const adminItems = [];
  if (has("catalogue.create_category", "catalogue.view")) adminItems.push({ to: "/admin/categories", icon: Tag, label: "Categories" });
  if (has("catalogue.manage_channels", "admin.full_access")) adminItems.push({ to: "/channels", icon: Store, label: "Sales Channels" });
  if (has("admin.manage_customer_types", "admin.full_access")) adminItems.push({ to: "/admin/customer-types", icon: UserCheck, label: "Customer Types" });
  if (has("admin.full_access")) adminItems.push({ to: "/admin/settings", icon: Settings, label: "System Settings" });
  if (has("admin.full_access")) adminItems.push({ to: "/admin/data-scripts", icon: Settings, label: "Data scripts" });
  if (has("admin.full_access")) adminItems.push({ to: "/admin/migrate-invoice-dates", icon: Settings, label: "Invoice Date Migration" });
  if (has("admin.full_access")) adminItems.push({ to: "/admin/channel-backfill", icon: Settings, label: "Channel Assignment Fix" });
  if (has("admin.full_access", "finance.view_reports")) adminItems.push({ to: "/admin/data-quality", icon: Settings, label: "Data Quality Reports" });
  if (has("admin.full_access")) adminItems.push({ to: "/admin/security", icon: ShieldAlert, label: "Security" });
  if (adminItems.length > 0) groups.push({ label: "Admin Setup", items: adminItems });

  // User Management
  const userItems = [];
  if (has("users.assign_roles", "users.deactivate", "admin.full_access")) userItems.push({ to: "/users", icon: Users, label: "Users" });
  if (has("users.create_custom_role", "admin.full_access")) userItems.push({ to: "/roles", icon: Shield, label: "Roles & Permissions" });
  if (has("users.add_to_own_team", "users.add_to_any_team", "admin.full_access")) userItems.push({ to: "/teams", icon: UsersRound, label: "Teams" });
  if (userItems.length > 0) groups.push({ label: "User Management", items: userItems });

  // Order Management
  const orderItems = [];
  if (has("customers.view_all", "customers.view_own", "customers.create", "admin.full_access")) orderItems.push({ to: "/customers", icon: Contact, label: "Customers" });
  if (has("orders.view_all", "orders.view_own", "orders.view_team", "orders.create", "finance.view_all_orders", "warehouse.view_orders", "admin.full_access")) orderItems.push({ to: "/orders", icon: ShoppingCart, label: "Orders" });
  // Returns (RTO) — visible to all roles that can see orders
  if (has("orders.view_all", "orders.view_own", "orders.view_team", "orders.create", "finance.view_all_orders", "warehouse.view_orders", "admin.full_access")) orderItems.push({ to: "/returns", icon: RotateCcw, label: "Returns (RTO)" });
  if (has("orders.create", "finance.view_reports", "admin.full_access")) orderItems.push({ to: "/proforma-invoices", icon: FileText, label: "Proforma Invoices" });
  if (has("finance.approve", "finance.view_all_orders", "admin.full_access") && !roles.some(r => ["Sales Executive", "Sales Manager"].includes(r) && !["Admin", "admin", "Finance Manager", "Finance Executive", "Finance — Invoicing Executive"].includes(r))) orderItems.push({ to: "/credit-notes", icon: CreditCard, label: "Credit Notes" });
  if (has("finance.view_all_orders", "admin.full_access")) orderItems.push({ to: "/external-ship-orders", icon: Truck, label: "Direct-Ship Orders" });
  if (has("po_inbox.view", "admin.full_access")) orderItems.push({ to: "/po-inbox", icon: Inbox, label: "PO Inbox" });
  if (has("orders.view_all", "finance.view_all_orders", "admin.full_access")) orderItems.push({ to: "/qcom-health", icon: TrendingUp, label: "Qcom Order Health" });
  if (has("pulse.view", "pulse.view_ads", "pulse.upload", "admin.full_access")) orderItems.push({ to: "/pulse", icon: BarChart3, label: "ORBIT Pulse" });
  if (has("demand_planning.view", "demand_planning.upload_projection", "admin.full_access")) orderItems.push({ to: "/demand-planning", icon: TrendingUp, label: "Demand Planning" });
  if (has("logistics.upload_actual_freight", "admin.full_access")) orderItems.push({ to: "/logistics/upload-freight", icon: Truck, label: "Upload Freight" });
  if (orderItems.length > 0) groups.push({ label: "Order Management", items: orderItems });

  // Inventory
  const invItems = [];
  if (has("catalogue.view", "catalogue.create_product", "catalogue.create_sku", "admin.full_access")) invItems.push({ to: "/catalogue", icon: BookOpen, label: "Product Catalogue" });
  if (has("catalogue.approve", "admin.full_access")) invItems.push({ to: "/catalogue/approvals", icon: ClipboardList, label: "Catalogue Approvals" });
  if (has("warehouse.view_orders", "warehouse.mark_packed", "admin.full_access")) invItems.push({ to: "/warehouses", icon: Warehouse, label: "Warehouses" });
  if (has("warehouse.view_orders", "warehouse.mark_packed", "admin.full_access")) invItems.push({ to: "/inventory", icon: BarChart3, label: "Stock & Inventory" });
  if (has("warehouse.view_orders", "orders.view_all", "ss_inventory.view", "admin.full_access")) invItems.push({ to: "/ss-inventory", icon: PackageOpen, label: "SS Inventory" });
  if (has("ss_pulse_sync.manage_mapping", "admin.full_access")) invItems.push({ to: "/ss-facility-mapping", icon: MapPin, label: "Facility → SS Mapping" });
  if (has("ss_platform_sku_map.manage", "admin.full_access")) invItems.push({ to: "/platform-sku-mapping", icon: Link2, label: "Platform SKU Mapping" });
  if (has("ss_inventory.record_in_transit", "ss_inventory.record_platform_delivery", "admin.full_access")) invItems.push({ to: "/ss-in-transit", icon: Truck, label: "In-Transit Deliveries" });
  if (has("warehouse.view_orders", "orders.view_all", "orders.view_own", "orders.create", "admin.full_access")) invItems.push({ to: "/rto-stock", icon: Package, label: "RTO Stock" });
  if (has("warehouse.mark_packed", "admin.full_access")) invItems.push({ to: "/damaged-stock", icon: Trash2, label: "Damaged Stock" });
  if (invItems.length > 0) groups.push({ label: "Inventory", items: invItems });

  // Reports
  if (has("reports.sales_dashboard", "reports.finance_dashboard", "reports.operations_dashboard", "reports.executive_dashboard", "reports.export_data", "finance.view_reports", "warehouse.view_reports", "reports.stock_on_hold", "finance.manage_platform_invoices", "finance.record_payment", "refunds.view", "refunds.approve", "refunds.pay", "pod.view", "pod.upload", "admin.full_access")) {
    const reportItems = [];
    if (has("reports.sales_dashboard", "reports.finance_dashboard", "reports.operations_dashboard", "reports.executive_dashboard", "reports.export_data", "finance.view_reports", "warehouse.view_reports", "admin.full_access")) {
      reportItems.push({ to: "/reports", icon: BarChart3, label: "Reports Centre" });
    }
    if (has("reports.qcom_sla_view", "admin.full_access")) {
      reportItems.push({ to: "/reports/qcom-sla-performance", icon: TrendingUp, label: "Qcom SLA Performance" });
    }
    if (has("targets.view", "targets.manage", "admin.full_access")) {
      reportItems.push({ to: "/sales-targets", icon: TrendingUp, label: "Sales Targets" });
    }
    if (has("reports.pnl_audit_view", "admin.full_access")) {
      reportItems.push({ to: "/reports/pnl-audit", icon: BarChart3, label: "PnL Audit Report" });
    }
    if (has("reports.receivables_view", "dashboard.receivables_view", "admin.full_access")) {
      reportItems.push({ to: "/reports/receivables", icon: BarChart3, label: "Payment Receivables" });
    }
    // BATCH 5 PART B (5.6a / 5.5b)
    if (has("reports.stock_on_hold", "admin.full_access")) {
      reportItems.push({ to: "/reports/stock-on-hold", icon: BarChart3, label: "Stock on Hold" });
    }
    if (has("finance.manage_platform_invoices", "admin.full_access")) {
      reportItems.push({ to: "/platform-invoices", icon: BarChart3, label: "Platform Invoices" });
    }
    // NEXT BUILD PASS 1B (item 11): sale-or-return receivables
    if (has("sor.view", "admin.full_access")) {
      reportItems.push({ to: "/sor-accounts", icon: BarChart3, label: "SOR Accounts" });
    }
    // PASS 2 (B3): the refund register
    if (has("refunds.view", "refunds.approve", "refunds.pay", "admin.full_access")) {
      reportItems.push({ to: "/refunds", icon: CreditCard, label: "Refunds" });
    }
    // PART C (C4): proof of delivery
    if (has("pod.view", "pod.upload", "admin.full_access")) {
      reportItems.push({ to: "/reports/pod", icon: BarChart3, label: "Proof of Delivery" });
    }
    if (has("finance.record_payment", "pulse.view_full", "admin.full_access")) {
      reportItems.push({ to: "/reports/blinkit-checks", icon: BarChart3, label: "Blinkit Checks" });
    }
    if (reportItems.length > 0) groups.push({ label: "Reports", items: reportItems });
  }

  // ORBIT OPS — appended at the bottom of nav
  const opsItems = [];
  if (has("item_master.create_draft", "item_master.view_specs", "item_master.view_cost", "admin.full_access")) opsItems.push({ to: "/ops/items", icon: ClipboardList, label: "Item Master" });
  if (has("vendor.create_draft", "vendor.view_basic", "vendor.view_pricing", "admin.full_access")) opsItems.push({ to: "/ops/vendors", icon: Factory, label: "Vendor Master" });
  if (has("location.create_draft", "location.view", "admin.full_access")) opsItems.push({ to: "/ops/locations", icon: MapPin, label: "Location Master" });
  if (has("bom.create_edit", "bom.view_quantities", "bom.view_costs", "admin.full_access")) opsItems.push({ to: "/ops/bom", icon: Layers, label: "BOM Master" });
  if (has("indent.create", "indent.approve", "indent.view", "admin.full_access")) opsItems.push({ to: "/ops/indents", icon: ClipboardList, label: "Indents" });
  if (has("po.create", "po.approve", "po.view", "admin.full_access")) opsItems.push({ to: "/ops/purchase-orders", icon: FileText, label: "Purchase Orders" });
  if (has("gate_pass.create", "gate_pass.view", "admin.full_access")) opsItems.push({ to: "/ops/gate-passes", icon: Truck, label: "Gate Pass IN" });
  if (has("grn.create", "grn.qc_post", "grn.view", "admin.full_access")) opsItems.push({ to: "/ops/grn", icon: PackageOpen, label: "GRN" });
  if (has("vendor_invoice.create", "vendor_invoice.stage1_approve", "vendor_invoice.final_approve", "vendor_invoice.view", "admin.full_access")) opsItems.push({ to: "/ops/vendor-invoices", icon: FileText, label: "Vendor Invoices" });
  if (has("payment_req.create", "payment_req.approve", "payment_req.execute", "payment_req.view", "admin.full_access")) opsItems.push({ to: "/ops/payment-requisitions", icon: CreditCard, label: "Payment Requisitions" });
  if (has("vendor_outstanding.view", "admin.full_access")) opsItems.push({ to: "/ops/vendor-outstanding", icon: BarChart3, label: "Vendor Outstanding" });
  if (has("ecom_stock.adjust", "ecom_stock.view", "admin.full_access")) opsItems.push({ to: "/ops/ecom-stock", icon: ShoppingCart, label: "Ecom Stock" });
  if (has("stock.view_fg", "stock.view_pm", "admin.full_access")) opsItems.push({ to: "/ops/stock", icon: BarChart3, label: "Stock Dashboard" });
  if (has("stock.packing_entry", "admin.full_access")) opsItems.push({ to: "/ops/packing", icon: Package, label: "Packing Entry" });
  if (has("stock.returns_receive", "stock.returns_qc", "admin.full_access")) opsItems.push({ to: "/ops/returns-inward", icon: RotateCcw, label: "Returns Inward" });
  if (has("stock.adjust", "stock.adjust_approve", "admin.full_access")) opsItems.push({ to: "/ops/stock-adjustments", icon: ClipboardList, label: "Stock Adjustments" });
  if (has("ops.stock_transfer", "admin.full_access") || roles.includes("Auditor")) opsItems.push({ to: "/ops/stock-transfers", icon: Truck, label: "Stock Transfers" });
  if (has("stock.opening_entry", "admin.full_access")) opsItems.push({ to: "/ops/opening-stock", icon: PackageOpen, label: "Opening Stock" });
  if (has("ops_config.view", "ops_config.edit", "admin.full_access")) opsItems.push({ to: "/ops/settings/config", icon: Settings2, label: "OPS Config" });
  if (opsItems.length > 0) groups.push({ label: "ORBIT OPS", items: opsItems });

  return groups;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = (user?.roles || [""])[0];
  const userPermissions = user?.permissions || [];
  const navGroups = getNavForPermissions(userPermissions, user?.roles || []);
  const { getItems: getCfgItems } = useConfig();
  const crmFlagOn = (getCfgItems("feature_flags") || []).some(i => i.key === "CRM_CORE" && i.value === "true");
  if (crmFlagOn) {
    const hasPerm = (...keys) => keys.some(k => userPermissions.includes(k));
    const crmItems = [];
    if (hasPerm("crm.view_own", "crm.edit", "crm.admin", "admin.full_access")) {
      crmItems.push({ to: "/crm/my-work", icon: ClipboardList, label: "My Work" });
      crmItems.push({ to: "/crm", icon: BarChart3, label: "Pipeline Board" });
    }
    if (hasPerm("crm.admin", "admin.full_access")) {
      crmItems.push({ to: "/crm/admin", icon: Settings2, label: "CRM Admin" });
      crmItems.push({ to: "/crm/exceptions", icon: ShieldAlert, label: "Exceptions" });
    }
    if (crmItems.length > 0) navGroups.push({ label: "CRM", items: crmItems });
  }
  const dpFlagOn = (getCfgItems("feature_flags") || []).some(i => i.key === "DP_VERTICAL" && i.value === "true");
  const brandsFlagOn = (getCfgItems("feature_flags") || []).some(i => i.key === "BRANDS" && i.value === "true");
  if (dpFlagOn) {
    const dpPerms = ["dp.partners.view", "dp.partners.create", "dp.partners.edit", "dp.partners.approve", "dp.admin", "dp.brands.manage", "admin.full_access"];
    if (dpPerms.some(k => userPermissions.includes(k))) {
      const dpItems = [
        { to: "/dp", icon: Store, label: "DP Pipeline" },
        { to: "/dp/apply", icon: FileText, label: "New Application" },
        { to: "/dp/search", icon: Search, label: "Pincode Search" },
      ];
      if (["dp.brands.manage", "dp.admin", "admin.full_access"].some(k => userPermissions.includes(k)) && !brandsFlagOn) {
        dpItems.push({ to: "/dp/brands", icon: Tag, label: "Brands" });
      }
      if (["dp.partners.approve", "dp.admin", "admin.full_access"].some(k => userPermissions.includes(k))) {
        dpItems.push({ to: "/dp/approvals", icon: ShieldAlert, label: "Territory Approvals" });
      }
      navGroups.push({ label: "Direct Partners", items: dpItems });
    }
  }
  const leadFlagOn = (getCfgItems("feature_flags") || []).some(i => i.key === "LEAD_DESK" && i.value === "true");
  if (leadFlagOn) {
    const leadPerms = ["leads.view", "leads.create", "leads.edit", "leads.route", "leads.convert", "leads.admin", "admin.full_access"];
    if (leadPerms.some(k => userPermissions.includes(k))) {
      const leadItems = [
        { to: "/leads", icon: Inbox, label: "Lead Desk" },
        { to: "/leads/new", icon: UserCheck, label: "Quick Add Lead" },
      ];
      if (["leads.import", "leads.admin", "admin.full_access"].some(k => userPermissions.includes(k))) {
        leadItems.push({ to: "/leads/import", icon: Upload, label: "Upload Leads" });
      }
      if (["leads.reports", "leads.admin", "admin.full_access"].some(k => userPermissions.includes(k))) {
        leadItems.push({ to: "/leads/campaign-report", icon: BarChart3, label: "Campaign Report" });
      }
      navGroups.push({ label: "Lead Desk", items: leadItems });
    }
  }
  if (brandsFlagOn) {
    const brandPerms = ["brands.view", "brands.create", "brands.edit", "brands.approve", "brands.suspend", "brands.view_contacts", "brands.view_kyc", "brands.view_terms", "brands.listings.manage", "brands.listings.push", "admin.full_access"];
    if (brandPerms.some(k => userPermissions.includes(k))) {
      const brandItems = [];
      if (["brands.view", "admin.full_access"].some(k => userPermissions.includes(k))) {
        brandItems.push({ to: "/brands", icon: Tag, label: "All Brands" });
      }
      if (["brands.create", "admin.full_access"].some(k => userPermissions.includes(k))) {
        brandItems.push({ to: "/brands/new", icon: FileText, label: "New Brand" });
      }
      if (["brands.approve", "admin.full_access"].some(k => userPermissions.includes(k))) {
        brandItems.push({ to: "/brands/approvals", icon: ShieldAlert, label: "Brand Approvals" });
      }
      brandItems.push({ to: "/brands/check", icon: MapPin, label: "Check Restriction" });
      if (["brands.edit", "admin.full_access"].some(k => userPermissions.includes(k))) {
        brandItems.push({ to: "/brands/unlinked-products", icon: Link2, label: "Unlinked Products" });
      }
      navGroups.push({ label: "Brands", items: brandItems });
    } else if (["dp.partners.view", "leads.route"].some(k => userPermissions.includes(k))) {
      // C6 — DP / Lead Desk users get the restriction checker only
      navGroups.push({ label: "Brands", items: [{ to: "/brands/check", icon: MapPin, label: "Check Restriction" }] });
    }
  }
  const claimsFlagOn = (getCfgItems("feature_flags") || []).some(i => i.key === "CLAIMS" && i.value === "true");
  if (claimsFlagOn) {
    const claimPerms = ["claims.view_own", "claims.create", "claims.review", "claims.final_approve",
      "claims.view_all", "claims.override_late", "claims.recovery.manage", "claims.settings", "admin.full_access"];
    if (claimPerms.some(k => userPermissions.includes(k))) {
      const claimItems = [{ to: "/claims", icon: ClipboardList, label: "Claims" }];
      if (["claims.create", "admin.full_access"].some(k => userPermissions.includes(k))) {
        claimItems.push({ to: "/claims/new", icon: FileText, label: "Raise Claim" });
      }
      if (["claims.view_all", "admin.full_access"].some(k => userPermissions.includes(k))) {
        claimItems.push({ to: "/claims/decisions", icon: ShieldAlert, label: "All Claims (Admin)" });
      }
      if (["claims.recovery.manage", "admin.full_access"].some(k => userPermissions.includes(k))) {
        claimItems.push({ to: "/claims/recoveries", icon: CreditCard, label: "Money to Get Back" });
      }
      if (["claims.settings", "admin.full_access"].some(k => userPermissions.includes(k))) {
        claimItems.push({ to: "/claims/settings", icon: Settings2, label: "Claim Rules" });
      }
      navGroups.push({ label: "Claims", items: claimItems });
    }
  }
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  useEffect(() => {
    const loadNotifs = async () => {
      try {
        const { data } = await api.get("/notifications?unread_only=true");
        setNotifications(data);
      } catch {}
    };
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length >= 2) {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setSearchResults(data.results || []);
        setShowSearch(true);
      } catch { setSearchResults([]); }
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleChangePassword = async () => {
    setPwError("");
    if (pwForm.new_password !== pwForm.confirm) { setPwError("New passwords do not match"); return; }
    if (pwForm.new_password.length < 8) { setPwError("Password must be at least 8 characters"); return; }
    if (!/[A-Z]/.test(pwForm.new_password)) { setPwError("Must contain at least one uppercase letter"); return; }
    if (!/[0-9]/.test(pwForm.new_password)) { setPwError("Must contain at least one number"); return; }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/]/.test(pwForm.new_password)) { setPwError("Must contain at least one special character"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.new_password });
      toast.success("Password changed. Please log in again.");
      setShowChangePw(false);
      await logout();
      navigate("/login");
    } catch (err) { setPwError(formatApiError(err.response?.data?.detail) || "Failed to change password"); }
    finally { setPwSaving(false); }
  };

  // Force change password screen
  if (user?.must_change_password) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-50" data-testid="force-change-password">
        <div className="w-full max-w-sm border border-zinc-200 bg-white p-6 rounded-sm">
          <h2 className="font-heading text-xl font-bold text-zinc-950 mb-1">Change Your Password</h2>
          <p className="text-xs text-zinc-500 mb-4">Your password was reset by an administrator. Please set a new password to continue.</p>
          {pwError && <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm">{pwError}</div>}
          <div className="space-y-3">
            <div><Label className="text-xs">Current Password</Label><Input type="password" value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})} className="mt-1 rounded-sm" data-testid="force-current-pw" /></div>
            <div><Label className="text-xs">New Password</Label><Input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})} className="mt-1 rounded-sm" data-testid="force-new-pw" /></div>
            <div><Label className="text-xs">Confirm New Password</Label><Input type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} className="mt-1 rounded-sm" data-testid="force-confirm-pw" /></div>
            <p className="text-[10px] text-zinc-400">Min 8 chars, 1 uppercase, 1 number, 1 special character</p>
            <Button onClick={handleChangePassword} disabled={pwSaving} className="w-full rounded-sm text-xs" data-testid="force-change-pw-btn">{pwSaving ? "Saving..." : "Set New Password"}</Button>
          </div>
        </div>
      </div>
    );
  }

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="flex min-h-screen bg-white overflow-hidden w-full max-w-[100vw]">
      {/* Mobile backdrop */}
      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-backdrop" />}

      {/* Sidebar — desktop: static, mobile: slide-in drawer */}
      <aside className={`sidebar ${sidebarOpen ? '' : 'collapsed'} fixed inset-y-0 left-0 z-50 transition-transform duration-200 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:relative md:z-auto`} data-testid="sidebar">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          {sidebarOpen && (
            <div>
              <h1 className="font-heading text-lg font-bold tracking-tight text-zinc-950" data-testid="app-logo">ORBIT 2.0</h1>
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-zinc-400">Business Management</p>
            </div>
          )}
          <button onClick={() => { setMobileMenuOpen(false); setSidebarOpen(!sidebarOpen); }} className="p-1 hover:bg-zinc-200 rounded-sm hidden md:block" data-testid="sidebar-toggle">
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
          <button onClick={() => setMobileMenuOpen(false)} className="p-1 hover:bg-zinc-200 rounded-sm md:hidden">
            <X size={16} />
          </button>
        </div>

        <nav className="p-2 space-y-1 overflow-y-auto" style={{maxHeight: "calc(100vh - 130px)"}} data-testid="sidebar-nav">
          {navGroups.map((group, gi) => (
            <div key={gi}>
              {group.label && sidebarOpen && (
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-3 py-2 mt-3 mb-1"
                  data-testid={`nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="text-[10px] font-semibold tracking-[0.08em] uppercase text-zinc-400">{group.label}</span>
                  {expandedGroups[group.label] === false ? <ChevronRight size={12} className="text-zinc-400" /> : <ChevronDown size={12} className="text-zinc-400" />}
                </button>
              )}
              {(expandedGroups[group.label] !== false || !group.label) && group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/" || item.to === "/reports"}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                >
                  <item.icon size={16} strokeWidth={1.5} />
                  {sidebarOpen && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {sidebarOpen && (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-zinc-200 bg-zinc-50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-zinc-900 truncate">{user?.name}</p>
                <p className="text-[10px] text-zinc-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200 rounded-sm transition-colors"
              data-testid="logout-button"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="main-content flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="app-header" data-testid="app-header">
          <div className="flex items-center gap-2 flex-1">
            {/* Mobile hamburger */}
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 hover:bg-zinc-100 rounded-sm md:hidden" data-testid="mobile-menu-btn">
              <Menu size={20} />
            </button>
            <div className="relative w-full max-w-sm hidden md:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder="Search orders, customers, SKUs..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                onFocus={() => searchResults.length > 0 && setShowSearch(true)}
                className="pl-9 h-8 text-xs rounded-sm border-zinc-200"
                data-testid="global-search-input"
              />
              {showSearch && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg z-50 max-h-64 overflow-y-auto" data-testid="search-results">
                  {searchResults.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => { navigate(r.link); setShowSearch(false); setSearchQuery(""); }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 text-left"
                    >
                      <span className="text-[10px] font-semibold uppercase text-zinc-400 w-14">{r.type}</span>
                      <div>
                        <p className="text-xs font-medium text-zinc-900">{r.title}</p>
                        <p className="text-[10px] text-zinc-500">{r.subtitle}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowNotifs(!showNotifs)}
                className="relative h-8 w-8"
                data-testid="notifications-bell"
              >
                <Bell size={16} strokeWidth={1.5} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
              {showNotifs && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-zinc-200 rounded-sm shadow-lg z-50" data-testid="notifications-dropdown">
                  <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-zinc-900">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={async () => { await api.put("/notifications/read-all"); setNotifications([]); }}
                        className="text-[10px] text-blue-600 hover:underline"
                        data-testid="mark-all-read"
                      >Mark all read</button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-xs text-zinc-400 text-center">No notifications</p>
                    ) : notifications.slice(0, 10).map((n) => (
                      <button key={n._id} onClick={async () => {
                        await api.put(`/notifications/${n._id}/read`).catch(() => {});
                        setShowNotifs(false);
                        // Navigate to order detail if link contains order ID
                        const link = n.link || "";
                        if (n.entity_id) { navigate(`/orders/${n.entity_id}`); }
                        else if (link.startsWith("/orders/")) { navigate(link); }
                        else { navigate(link || "/orders"); }
                      }} className={`w-full text-left p-3 border-b border-zinc-50 hover:bg-zinc-50 ${n.read ? 'opacity-60' : ''}`}>
                        <p className="text-xs font-medium text-zinc-900">{n.title}</p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">{n.message}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="h-6 w-px bg-zinc-200 mx-1" />
            <div className="relative">
              <button onClick={() => setShowProfileMenu(!showProfileMenu)} className="flex items-center gap-2 hover:bg-zinc-100 rounded-sm px-2 py-1 transition-colors" data-testid="profile-avatar-btn">
                <div className="w-7 h-7 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-xs font-medium text-zinc-900">{user?.name}</p>
                  <p className="text-[10px] text-zinc-400">{user?.roles?.join(', ')}</p>
                </div>
              </button>
              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-zinc-200 rounded-sm shadow-lg z-50" data-testid="profile-dropdown">
                  <button onClick={() => { setShowProfileMenu(false); navigate("/profile"); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50" data-testid="profile-menu-profile">
                    <User size={14} /> My Profile
                  </button>
                  <button onClick={() => { setShowProfileMenu(false); setShowChangePw(true); setPwForm({ current: "", new_password: "", confirm: "" }); setPwError(""); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50" data-testid="profile-menu-change-pw">
                    <KeyRound size={14} /> Change Password
                  </button>
                  <div className="border-t border-zinc-100" />
                  <button onClick={() => { setShowProfileMenu(false); handleLogout(); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50" data-testid="profile-menu-signout">
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-3 md:p-6 lg:p-8 overflow-x-hidden overflow-y-auto max-w-full" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={showChangePw} onOpenChange={setShowChangePw}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="font-heading">Change Password</DialogTitle></DialogHeader>
          {pwError && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm">{pwError}</div>}
          <div className="space-y-3">
            <div><Label className="text-xs">Current Password</Label><Input type="password" value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})} className="mt-1 rounded-sm" data-testid="change-pw-current" /></div>
            <div><Label className="text-xs">New Password</Label><Input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})} className="mt-1 rounded-sm" data-testid="change-pw-new" /></div>
            <div><Label className="text-xs">Confirm New Password</Label><Input type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} className="mt-1 rounded-sm" data-testid="change-pw-confirm" /></div>
            <p className="text-[10px] text-zinc-400">Min 8 chars, 1 uppercase, 1 number, 1 special character</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePw(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleChangePassword} disabled={pwSaving} className="rounded-sm text-xs" data-testid="change-pw-submit">{pwSaving ? "Saving..." : "Change Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
